import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranchIcon } from '@phosphor-icons/react';
import { Button, Dialog, EmptyState } from '@origo/origo-ui';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import RelationCard from './components/RelationCard';
import RelationEditDialog, { type RelationEditDialogOptions } from './components/RelationEditDialog';
import type { RelationFormValues, RelationPair } from '../../types';
import styles from './RelationsPage.module.css';

interface RelationsPageProps {
    pairs: RelationPair[];
    dialogOpen: boolean;
    dialogValues: RelationFormValues;
    dialogOptions: RelationEditDialogOptions;
    onEdit: (id: string) => void;
    onDialogChange: (field: keyof RelationFormValues, value: string) => void;
    onDialogClose: () => void;
    onDialogSave: () => void;
}

const RelationsPage: React.FC<RelationsPageProps> = ({
    pairs,
    dialogOpen,
    dialogValues,
    dialogOptions,
    onEdit,
    onDialogChange,
    onDialogClose,
    onDialogSave,
}) => {
    const { t } = useTranslation();
    const [stubOpen, setStubOpen] = useState(false);

    const isEmpty = pairs.length === 0;

    return (
        <article className={styles.page}>
            <PlanDetailHeader title={t('relations.title')} subtitle={t('relations.subtitle')} />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<GitBranchIcon size={32} weight="duotone" />}
                        title={t('relations.empty.title')}
                        description={t('relations.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={() => setStubOpen(true)}>
                                {t('relations.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <>
                        <h2 className={styles.groupTitle}>{t('relations.groupTitle')}</h2>
                        <ul className={styles.list}>
                            {pairs.map(pair => (
                                <li key={pair.id}>
                                    <RelationCard pair={pair} onEdit={onEdit} />
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
            <RelationEditDialog
                open={dialogOpen}
                values={dialogValues}
                options={dialogOptions}
                onChange={onDialogChange}
                onClose={onDialogClose}
                onSave={onDialogSave}
            />
            <Dialog
                open={stubOpen}
                onClose={() => setStubOpen(false)}
                title={t('relations.actionStub.title')}
                footer={
                    <Button variant="primary" onClick={() => setStubOpen(false)}>
                        {t('relations.actionStub.close')}
                    </Button>
                }
            >
                <p>{t('relations.actionStub.body')}</p>
            </Dialog>
        </article>
    );
};

export default RelationsPage;
